import { useEffect } from 'react';
import { useAuthStore } from '../store';
import { LoadingSpinner, ErrorMessage, Button } from './ui';

interface AuthGuardProps {
  children: React.ReactNode;
}

export const AuthGuard = ({ children }: AuthGuardProps) => {
  const { isAuthenticated, isLoading, error, user, initialize } = useAuthStore();

  useEffect(() => {
    initialize();
  }, [initialize]);

  // Show loading while initializing
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <LoadingSpinner size="large" className="mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Initializing App
          </h2>
          <p className="text-gray-600">
            Please wait while we set up your session...
          </p>
        </div>
      </div>
    );
  }

  // Show error if authentication failed
  if (error || !isAuthenticated) {
    const isTokenError = error?.includes('token') || error?.includes('session') || error?.includes('authentication');
    
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg max-w-md w-full">
          <div className="text-center p-6 border-b">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">🔐</span>
            </div>
            <h2 className="text-xl font-semibold text-yellow-600 mb-2">
              {isTokenError ? 'Authentication Required' : 'Access Denied'}
            </h2>
            <p className="text-gray-600">
              {error || 'You need to be authenticated to access this app.'}
            </p>
          </div>

          <div className="p-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h3 className="font-medium text-blue-900 mb-2">How to fix this:</h3>
              <ol className="text-sm text-blue-800 list-decimal list-inside space-y-1">
                <li>Close this app completely</li>
                <li>Open the app again from the main application</li>
                <li>Ensure you have proper permissions</li>
              </ol>
            </div>

            <Button
              onClick={() => window.location.reload()}
              variant="primary"
              size="large"
              className="w-full"
            >
              Reload App
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show error if user data is missing
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <ErrorMessage
          title="User Data Missing"
          message="Unable to load your user information. Please close and reopen the app."
          action={
            <Button 
              onClick={() => window.location.reload()} 
              variant="primary" 
              size="small"
            >
              Reload
            </Button>
          }
        />
      </div>
    );
  }

  // Render the app if everything is good
  return <>{children}</>;
};